import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useEvents } from '../contexts/EventContext';

/**
 * Operator mobile home (Phase 1) — the pillar-aware landing screen for
 * CUSTOMER-TIER roles on a phone. It leads with the event switcher + a compact
 * "today" summary, then shows one band per ENABLED pillar (Giving / Events),
 * reusing the same endpoints the desktop dashboard already uses. Pillars the
 * tenant hasn't enabled are simply omitted.
 *
 * This renders only on mobile for customer-tier roles (gated by the caller in
 * DashboardPage). Desktop and legacy/attendee flows are untouched.
 */

interface PortfolioSummary {
  activeCount: number;
  totalGuests: number;
  totalTicketsSold: number;
  totalCheckedIn: number;
}

interface GivingSummary {
  campaignCount: number;
  totalRaised: number;
  combinedGoal: number;
  donorCount: number;
  percentToGoal: number;
}

const PRIMARY = '#1a6b3c';
const DARK = '#0f2d1f';
const GIVING = '#15803d';
const MUTED = '#6b7280';

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function money(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

export function OperatorMobileHomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { events, selectedEvent, selectEvent } = useEvents();

  const [tenantModules, setTenantModules] = useState<string[]>([]);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [portfolio, setPortfolio] = useState<PortfolioSummary | null>(null);
  const [giving, setGiving] = useState<GivingSummary | null>(null);
  const [switcherOpen, setSwitcherOpen] = useState(false);

  // Tenant pillars + portfolio once per user.
  useEffect(() => {
    let cancelled = false;
    apiClient<{ tenantModules?: string[]; tenantName?: string | null }>('/auth/me')
      .then((r) => {
        if (cancelled) return;
        const mods = r?.tenantModules ?? [];
        setTenantModules(mods);
        setTenantName(r?.tenantName ?? null);
        if (mods.includes('giving')) {
          apiClient<GivingSummary>('/dashboard/giving-summary')
            .then((g) => { if (!cancelled) setGiving(g); })
            .catch(() => { if (!cancelled) setGiving(null); });
        }
      })
      .catch(() => { if (!cancelled) setTenantModules([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiClient<PortfolioSummary>('/dashboard/portfolio')
      .then((p) => { if (!cancelled) setPortfolio(p); })
      .catch(() => { if (!cancelled) setPortfolio(null); });
    return () => { cancelled = true; };
  }, []);

  const firstName = useMemo(() => {
    const name = (user?.fullName || '').trim();
    if (!name || name.toLowerCase() === 'tenant admin') return 'there';
    return name.split(' ')[0];
  }, [user?.fullName]);

  const hasGiving = tenantModules.includes('giving');

  return (
    <div style={styles.page}>
      {/* Top bar: tenant + event switcher */}
      <div style={styles.topbar}>
        <div style={styles.topRow}>
          <span style={styles.tenantName}>{tenantName || 'Your workspace'}</span>
          <span style={styles.avatar}>{(firstName[0] || '?').toUpperCase()}</span>
        </div>
        <button
          style={styles.switcher}
          onClick={() => setSwitcherOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={switcherOpen}
        >
          <span style={styles.switcherLabel}>
            ▸ {selectedEvent ? selectedEvent.name : 'All events'}
          </span>
          <span style={styles.switcherChevron}>⌄</span>
        </button>
        {switcherOpen && (
          <div style={styles.switcherMenu} role="listbox">
            <button
              style={styles.switcherItem}
              onClick={() => { selectEvent(null); setSwitcherOpen(false); }}
              role="option"
              aria-selected={!selectedEvent}
            >
              All events (portfolio)
            </button>
            {events.map((e) => (
              <button
                key={e.id}
                style={styles.switcherItem}
                onClick={() => { selectEvent(e.id); setSwitcherOpen(false); navigate('/manage-event'); }}
                role="option"
                aria-selected={selectedEvent?.id === e.id}
              >
                {e.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Greeting + today summary */}
      <div style={styles.body}>
        <h1 style={styles.greeting}>{greeting()}, {firstName}.</h1>
        <p style={styles.summaryLine}>
          {portfolio ? portfolio.activeCount : '…'} active event
          {portfolio && portfolio.activeCount === 1 ? '' : 's'}
          {' · '}
          {portfolio ? portfolio.totalGuests.toLocaleString() : '…'} guests
          {hasGiving && giving ? ` · ${giving.campaignCount} campaign${giving.campaignCount === 1 ? '' : 's'}` : ''}
        </p>

        {/* GIVING band — only when the pillar is enabled */}
        {hasGiving && (
          <section style={{ ...styles.band, ...styles.givingBand }}>
            <div style={styles.bandHead}>
              <span style={{ ...styles.bandTitle, color: GIVING }}>💚 GIVING</span>
              <button style={{ ...styles.bandLink, color: GIVING }} onClick={() => navigate('/giving')}>
                View →
              </button>
            </div>
            {giving && giving.campaignCount > 0 ? (
              <>
                <div style={styles.statRow}>
                  <div style={styles.statCard}>
                    <div style={{ ...styles.statValue, color: GIVING }}>{money(giving.totalRaised)}</div>
                    <div style={styles.statLabel}>Raised</div>
                  </div>
                  <div style={styles.statCard}>
                    <div style={styles.statValue}>{giving.percentToGoal}%</div>
                    <div style={styles.statLabel}>to {money(giving.combinedGoal)} goal</div>
                  </div>
                </div>
                <div style={styles.track}>
                  <div style={{ ...styles.fill, width: `${Math.min(100, giving.percentToGoal)}%` }} />
                </div>
                <div style={styles.bandFoot}>{giving.donorCount} donors</div>
              </>
            ) : (
              <button style={styles.emptyCta} onClick={() => navigate('/giving?new=1')}>
                + Start your first campaign
              </button>
            )}
          </section>
        )}

        {/* EVENTS band — always relevant for the platform */}
        <section style={{ ...styles.band, ...styles.eventsBand }}>
          <div style={styles.bandHead}>
            <span style={{ ...styles.bandTitle, color: PRIMARY }}>🎟️ EVENTS</span>
            <button
              style={{ ...styles.bandLink, color: PRIMARY }}
              onClick={() => navigate(selectedEvent ? '/manage-event?tab=information' : '/events')}
            >
              View →
            </button>
          </div>
          <div style={styles.statRow}>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{portfolio ? portfolio.totalTicketsSold.toLocaleString() : '…'}</div>
              <div style={styles.statLabel}>Tickets sold</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{portfolio ? portfolio.totalGuests.toLocaleString() : '…'}</div>
              <div style={styles.statLabel}>Guests</div>
            </div>
          </div>
          <div style={styles.bandFoot}>
            {portfolio ? portfolio.totalCheckedIn.toLocaleString() : '…'} checked in
          </div>
        </section>

        {/* Quick actions */}
        <div style={styles.actions}>
          {hasGiving && (
            <button style={styles.primaryBtn} onClick={() => navigate('/giving?new=1')}>
              + New campaign
            </button>
          )}
          <button style={styles.secondaryBtn} onClick={() => navigate('/events')}>
            Manage events
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100%', backgroundColor: '#f9fafb' },
  topbar: { backgroundColor: DARK, padding: '14px 16px 16px', position: 'relative' },
  topRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  tenantName: { color: '#9fc7af', fontSize: '0.78rem', fontWeight: 600 },
  avatar: {
    width: 28, height: 28, borderRadius: '50%', backgroundColor: PRIMARY, color: '#fff',
    fontSize: '0.72rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  switcher: {
    width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', background: 'rgba(255,255,255,0.12)', border: 'none',
    borderRadius: 15, padding: '8px 14px', cursor: 'pointer',
  },
  switcherLabel: { color: '#fff', fontSize: '0.9rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  switcherChevron: { color: '#9fc7af', fontSize: '0.9rem' },
  switcherMenu: {
    position: 'absolute', left: 16, right: 16, top: '100%', marginTop: -4, backgroundColor: '#fff',
    borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.25)', zIndex: 50, overflow: 'hidden', maxHeight: '50vh', overflowY: 'auto',
  },
  switcherItem: {
    display: 'block', width: '100%', textAlign: 'left', padding: '13px 16px', background: 'none',
    border: 'none', borderBottom: '1px solid #f4f6f5', fontSize: '0.9rem', color: DARK, cursor: 'pointer',
  },
  body: { padding: '16px' },
  greeting: { fontSize: '1.3rem', fontWeight: 800, color: DARK, margin: '0 0 4px' },
  summaryLine: { fontSize: '0.82rem', color: MUTED, margin: '0 0 16px' },
  band: { borderRadius: 14, padding: '14px', marginBottom: 14, border: '1px solid' },
  givingBand: { backgroundColor: '#f0f8f3', borderColor: '#cde8d8' },
  eventsBand: { backgroundColor: '#f1f5f9', borderColor: '#dbe3ea' },
  bandHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  bandTitle: { fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.03em' },
  bandLink: { background: 'none', border: 'none', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' },
  statRow: { display: 'flex', gap: 10, marginBottom: 10 },
  statCard: { flex: 1, backgroundColor: '#fff', borderRadius: 10, padding: '10px 12px', border: '1px solid #eef1f0' },
  statValue: { fontSize: '1.2rem', fontWeight: 800, color: DARK, lineHeight: 1.1 },
  statLabel: { fontSize: '0.68rem', color: MUTED, marginTop: 3 },
  track: { height: 8, borderRadius: 4, backgroundColor: '#d9ece1', overflow: 'hidden', marginBottom: 8 },
  fill: { height: '100%', borderRadius: 4, backgroundColor: GIVING },
  bandFoot: { fontSize: '0.78rem', color: '#374151' },
  emptyCta: {
    width: '100%', padding: '12px', backgroundColor: '#fff', border: '1px dashed #9fc7af',
    borderRadius: 10, color: GIVING, fontSize: '0.86rem', fontWeight: 700, cursor: 'pointer',
  },
  actions: { display: 'flex', gap: 10, marginTop: 4 },
  primaryBtn: {
    flex: 1, padding: '12px', backgroundColor: PRIMARY, color: '#fff', border: 'none',
    borderRadius: 10, fontSize: '0.86rem', fontWeight: 700, cursor: 'pointer',
  },
  secondaryBtn: {
    flex: 1, padding: '12px', backgroundColor: '#eef3f0', color: PRIMARY, border: '1px solid #cde0d4',
    borderRadius: 10, fontSize: '0.86rem', fontWeight: 700, cursor: 'pointer',
  },
};
